// Sector tabs for the meetstandaard effect picker, derived from the catalog
// itself rather than a hardcoded list.
//
// The tabs used to be written out by hand, so an effect in a sector nobody had
// added yet was only reachable under "Alle" — invisible to anyone filtering.
// Deriving them means a new sector in the api (energiearmoede) appears without
// a code change; only its label lives locally, in sectorCatalog.js.
//
// AI-CONTEXT: See src/helpers/meetstandaard/sectorCatalog.js

import { getSectorInfo } from './sectorCatalog'

/**
 * @typedef {object} SectorTab
 * @property {string} id - Sector slug, or 'all' for the unfiltered tab
 * @property {string} label - Display label
 * @property {number} count - Effects in this sector ('all' = every effect)
 */

/**
 * Builds the tab list for the effect picker.
 *
 * An effect that belongs to several sectors is counted in each of them, which
 * matches how the tabs filter (`sectors.includes(tab.id)`). The 'all' count is
 * the number of effects, so it can be lower than the sum of the sector counts.
 *
 * @param {Array<{effects?: Array<{sectors?: string[]}>}>|null|undefined} effectDatabase - meetstandaard-api /database response
 * @returns {Array<SectorTab>} 'all' first, then one tab per sector, slug-sorted
 */
const buildSectorTabs = (effectDatabase) => {
    const categories = Array.isArray(effectDatabase) ? effectDatabase : []

    const effects = categories.flatMap((category) =>
        Array.isArray(category?.effects) ? category.effects : []
    )

    /** @type {Map<string, number>} */
    const countsBySector = new Map()
    effects.forEach((effect) => {
        const sectors = Array.isArray(effect?.sectors) ? effect.sectors : []
        sectors.forEach((slug) => {
            if (!slug || typeof slug !== 'string') return
            countsBySector.set(slug, (countsBySector.get(slug) || 0) + 1)
        })
    })

    const sectorTabs = [...countsBySector.keys()]
        .sort()
        .map((slug) => ({
            id: slug,
            // Unknown slugs fall back to the slug itself — the local catalog may
            // lag the api, and a missing label must not hide a whole sector.
            label: getSectorInfo(slug).label,
            count: countsBySector.get(slug),
        }))

    return [{ id: 'all', label: 'Alle', count: effects.length }, ...sectorTabs]
}

export default buildSectorTabs
